/**
 * starter-pack-result.js
 * Personalised Starter Pack — result flow engine
 * Module: StarterPackResult (IIFE)
 */
(function () {
  'use strict';

  var StarterPackResult = {

    // ─── CONFIG ─────────────────────────────────────────────────────────────
    Config: {
      API_BASE: 'https://lvi88fhcpg.execute-api.ap-southeast-2.amazonaws.com/prod/recommendation/quiz/quick-submission',
      discountCode: 'GETSTARTED',
      DEFAULT_FREQUENCY: '60',
      MIN_PRODUCTS: 1,
      COLLECTION_ID: '512297271595', // overridden from personalised config on init

      FREQUENCY_OPTIONS: {
        '30': { label: '1 month supply',  period: 'Every 30 days',  multiplier: 1, discount: 0,    freeShipping: false, badge: null },
        '60': { label: '2 month supply',  period: 'Every 60 days',  multiplier: 2, discount: 0.15, freeShipping: true,  badge: null },
        '90': { label: '3 month supply',  period: 'Every 90 days',  multiplier: 3, discount: 0.20, freeShipping: true,  badge: 'BEST VALUE' }
      },

      SELLING_PLAN_LABELS: {
        '30': '30_day_subscription',
        '60': '60_day_subscription_with_15%_discount',
        '90': '90_day_subscription_with_20%_discount'
      },

      MAX_ADDONS: 3,
      FREE_SHIPPING_THRESHOLD: 50
    },

    // ─── STATE ──────────────────────────────────────────────────────────────
    State: {
      currentStep: 'loading',
      token: null,
      packData: null,
      removedProducts: [],
      addedAddons: [],
      hasAddons: false,
      summaryListOpen: false,
      selectedFrequency: '60',
      syncInProgress: false,
      checkoutInProgress: false
    },

    // ─── HELPERS ────────────────────────────────────────────────────────────
    Helpers: {
      esc: function (str) {
        var el = document.createElement('span');
        el.textContent = str || '';
        return el.innerHTML;
      }
    },

    // ─── NORMALIZER ────────────────────────────────────────────────────────
    // Converts V3 hydrated ShopifyProduct[] backend response into the flat
    // internal shape expected by BundlePopulator, StepRenderer, and Analytics.
    Normalizer: {
      normalizeBackendResponse: function (rawData) {
        var self = StarterPackResult.Normalizer;
        var products = (rawData.products || [])
          .map(function (p) { return self.normalizeProduct(p, false); })
          .filter(function (p) { return p !== null; });

        var secondaryProducts = (rawData.secondaryProducts || [])
          .map(function (p) { return self.normalizeProduct(p, true); })
          .filter(function (p) { return p !== null; });

        return {
          products: products,
          optional_addons: secondaryProducts,
          goals: rawData.goals || [],
          pregnancy: rawData.pregnancy || '',
          allergy: rawData.allergy || '',
          describeDiets: rawData.describeDiets || '',
          raw_response: rawData
        };
      },

      normalizeProduct: function (product, isSecondary) {
        if (!product || !product.id) {
          console.warn('[SP Normalizer] Skipping product with missing id:', product);
          return null;
        }

        var selectedVariant = StarterPackResult.Normalizer.extractSelectedVariant(product);
        if (!selectedVariant) {
          console.warn('[SP Normalizer] Skipping product with no valid variant:', product.id, product.title);
          return null;
        }

        var productId = String(product.id);
        var variantId = String(selectedVariant.id);

        return {
          product_id: productId,
          variant_id: variantId,
          handle: product.handle || '',
          variant_sku: selectedVariant.sku || '',
          title: product.title || '',
          image_url: StarterPackResult.Normalizer.extractImageUrl(product, productId),
          reason: StarterPackResult.Normalizer.extractReason(product, selectedVariant),
          is_min_cap: product.is_min_cap || false,
          is_secondary: isSecondary,
          recoDosage: product.recoDosage || '',
          recoIngredients: product.recoIngredients || '',
          recoAllergens: product.recoAllergens || '',
          recoBody: product.recoBody || '',
          recoDisclaimer: product.recoDisclaimer || '',
          qualityIcons: product.qualityIcons || [],
          labelImage: product.labelImage || null
        };
      },

      extractSelectedVariant: function (product) {
        var variants = product.variants;
        if (!variants || variants.length === 0) return null;

        // Primary: first variant (backend places selected variant first)
        var selected = variants[0];

        // Safety: if first variant has no recommendations and another does, prefer that
        if ((!selected.recommendations || selected.recommendations.length === 0) && variants.length > 1) {
          for (var i = 1; i < variants.length; i++) {
            if (variants[i].recommendations && variants[i].recommendations.length > 0) {
              selected = variants[i];
              break;
            }
          }
        }

        return selected.id ? selected : null;
      },

      extractImageUrl: function (product, productId) {
        var pd = (window.personalised && window.personalised.productData || {})[productId];
        if (pd && pd.metafields && pd.metafields.library_image) return pd.metafields.library_image;
        return '';
      },

      extractReason: function (product, selectedVariant) {
        var self = StarterPackResult.Normalizer;

        if (selectedVariant && selectedVariant.recommendations && selectedVariant.recommendations.length > 0) {
          var reco = selectedVariant.recommendations[0];
          var fields = reco.fields || [];

          var reasonValue = self.getFieldValue(fields, 'for_your_reason');
          if (reasonValue) return reasonValue;

          var goalCategory = self.getFieldValue(fields, 'goal_category');
          if (goalCategory) return 'Recommended for ' + goalCategory.charAt(0).toUpperCase() + goalCategory.slice(1);
        }

        if (product.is_min_cap) {
          if (selectedVariant && selectedVariant.min_cap_for_your_reason) return selectedVariant.min_cap_for_your_reason;
          return 'A daily essential to support your health';
        }

        return 'Personalised for your health goals';
      },

      getFieldValue: function (fields, key) {
        for (var i = 0; i < fields.length; i++) {
          if (fields[i].key === key && fields[i].value) return fields[i].value;
        }
        return null;
      }
    },

    // ─── PRICE HELPER ───────────────────────────────────────────────────────
    calculateBaseMonthlyPrice: function (items) {
      var total = 0;
      var productData = window.personalised && window.personalised.productData;
      if (!productData || !items) return total;

      items.forEach(function (item) {
        var pd = productData[item.productId];
        if (pd && pd.variants && pd.variants.length > 0) {
          var variant = null;
          for (var i = 0; i < pd.variants.length; i++) {
            if (pd.variants[i].id === item.variantId) {
              variant = pd.variants[i];
              break;
            }
          }
          if (variant) {
            // price is in cents from Liquid injection
            total += variant.price / 100;
          }
        }
      });

      return total;
    },

    // ─── FETCHER ────────────────────────────────────────────────────────────
    Fetcher: {
      fetchPack: function () {
        var token = StarterPackResult.State.token;

        if (!token) {
          this.showError('missing_token', 'No pack token found. Please retake the quiz.');
          return;
        }

        var url = StarterPackResult.Config.API_BASE + '?token=' + encodeURIComponent(token);

        fetch(url, {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        })
          .then(function (response) {
            if (response.status === 404) {
              throw { code: 'not_found', message: 'Pack not found. It may have expired.' };
            }
            if (!response.ok) {
              throw { code: 'server_error', message: 'Server error (' + response.status + ') — please try again.' };
            }
            return response.json();
          })
          .then(function (data) {
            // Normalise V3 hydrated response to internal flat shape
            data = StarterPackResult.Normalizer.normalizeBackendResponse(data);

            // Guard: empty pack
            if (!data.products || data.products.length === 0) {
              StarterPackResult.Fetcher.showError('empty_pack', 'Your pack couldn\u2019t be built. Please try the quiz again.');
              return;
            }

            // Sync safety values to localStorage (belt-and-suspenders with quiz write)
            StarterPackResult.Disclaimer.syncSafetyValues(data);

            StarterPackResult.State.packData = data;

            // Populate bundle with recommended products
            StarterPackResult.BundlePopulator.populate(data);

            // Transition to reveal
            StarterPackResult.State.currentStep = 'reveal';
            StarterPackResult.StepRenderer.showStep('reveal');
            StarterPackResult.StepRenderer.renderReveal(data);

            // Show page-load disclaimer if user has pregnancy/allergy flags
            StarterPackResult.Disclaimer.showPageLoadModal(data);

            StarterPackResult.Analytics.packRevealed(data);
          })
          .catch(function (err) {
            var code = (err && err.code) || 'network_error';
            var message = (err && err.message) || 'Failed to load your pack. Please check your connection and try again.';
            StarterPackResult.Fetcher.showError(code, message);
          });
      },

      showError: function (code, message) {
        console.error('[SP Result] Error (' + code + '):', message);
        StarterPackResult.State.currentStep = 'error';

        var loadingEl = document.getElementById('sp-result-loading');
        var errorEl = document.getElementById('sp-result-error');
        var errorText = document.getElementById('sp-result-error-text');

        if (loadingEl) loadingEl.hidden = true;
        if (errorEl) errorEl.hidden = false;
        if (errorText) errorText.textContent = message;

        var retryBtn = document.getElementById('sp-result-error-retry');
        if (retryBtn) {
          retryBtn.addEventListener('click', function () {
            if (errorEl) errorEl.hidden = true;
            if (loadingEl) loadingEl.hidden = false;
            StarterPackResult.State.currentStep = 'loading';
            StarterPackResult.Fetcher.fetchPack();
          }, { once: true });
        }
      }
    },

    // ─── BUNDLE POPULATOR ───────────────────────────────────────────────────
    BundlePopulator: {
      populate: function (data) {
        var p = window.personalised;
        if (!p || !p.bundle || !p.utils || !p.cartSync) {
          console.error('[SP Result] window.personalised bundle infrastructure not ready');
          return;
        }

        var bundle = p.bundle;
        var utils = p.utils;
        var sellingPlans = p.sellingPlans || {};
        var collectionId = (p.config && p.config.bundle && p.config.bundle.RECHARGE && p.config.bundle.RECHARGE.COLLECTION_ID)
          || StarterPackResult.Config.COLLECTION_ID;

        // 1. Set mode to 'guest' FIRST
        utils.setCurrentMode('guest', { source: 'starter-pack' });

        // 2. Set default frequency (does NOT trigger cart sync)
        bundle.setFrequency(StarterPackResult.Config.DEFAULT_FREQUENCY);

        // 3. Clear any existing bundle items
        var currentItems = bundle.getItems();
        if (currentItems && currentItems.length > 0) {
          currentItems.slice().forEach(function (item) {
            bundle.removeItemByVariantId(item.variantId);
          });
        }

        // 4. Add each recommended product synchronously
        var frequency = bundle.getFrequency() || StarterPackResult.Config.DEFAULT_FREQUENCY;
        var sellingPlanLabel = StarterPackResult.Config.SELLING_PLAN_LABELS[frequency];

        (data.products || []).forEach(function (product) {
          var productId = utils.normalizeShopifyId(product.product_id);
          var variantId = utils.normalizeShopifyId(product.variant_id);

          if (productId === null || variantId === null) {
            console.warn('[SP Result] Invalid product/variant ID skipped:', product.product_id, product.variant_id);
            return;
          }

          var sellingPlanId = (sellingPlans[variantId] && sellingPlans[variantId][sellingPlanLabel]) || null;

          if (!sellingPlanId) {
            console.error('[SP Result] Missing selling plan for variant:', variantId, 'label:', sellingPlanLabel, 'product:', product.product_id);
          }

          bundle.addItem({
            productId: productId,
            variantId: variantId,
            collectionId: collectionId,
            quantity: 1,
            sellingPlanId: sellingPlanId
          });
        });

        // 5. Trigger cart sync AFTER all items added
        if (typeof p.cartSync.scheduleSync === 'function') {
          p.cartSync.scheduleSync('starter-pack-initial-populate');
        }
      },

      addAddon: function (product) {
        var p = window.personalised;
        if (!p || !p.bundle) return;

        var bundle = p.bundle;
        var utils = p.utils;
        var sellingPlans = p.sellingPlans || {};
        var collectionId = (p.config && p.config.bundle && p.config.bundle.RECHARGE && p.config.bundle.RECHARGE.COLLECTION_ID)
          || StarterPackResult.Config.COLLECTION_ID;

        var frequency = bundle.getFrequency() || StarterPackResult.Config.DEFAULT_FREQUENCY;
        var sellingPlanLabel = StarterPackResult.Config.SELLING_PLAN_LABELS[frequency];

        var productId = utils.normalizeShopifyId(product.product_id);
        var variantId = utils.normalizeShopifyId(product.variant_id);

        if (productId === null || variantId === null) {
          console.warn('[SP Result] Invalid addon product/variant ID:', product.product_id, product.variant_id);
          return;
        }

        var sellingPlanId = (sellingPlans[variantId] && sellingPlans[variantId][sellingPlanLabel]) || null;

        bundle.addItem({
          productId: productId,
          variantId: variantId,
          collectionId: collectionId,
          quantity: 1,
          sellingPlanId: sellingPlanId
        });

        StarterPackResult.State.addedAddons.push(product);
        StarterPackResult.Analytics.addonAdded(product);

        if (typeof p.cartSync.scheduleSync === 'function') {
          p.cartSync.scheduleSync('starter-pack-addon-added');
        }
      },

      removeProduct: function (variantId) {
        var p = window.personalised;
        if (!p || !p.bundle) return false;

        var bundle = p.bundle;
        var items = bundle.getItems();

        if (items.length <= StarterPackResult.Config.MIN_PRODUCTS) {
          StarterPackResult.StepRenderer.showLastItemModal();
          return false;
        }

        var normalizedId = p.utils.normalizeShopifyId(variantId);
        bundle.removeItemByVariantId(normalizedId);

        StarterPackResult.State.removedProducts.push(variantId);
        StarterPackResult.Analytics.productRemoved(variantId);

        if (typeof p.cartSync.scheduleSync === 'function') {
          p.cartSync.scheduleSync('starter-pack-product-removed');
        }

        return true;
      }
    },

    // ─── STEP RENDERER ──────────────────────────────────────────────────────
    StepRenderer: {
      showStep: function (stepName) {
        var steps = document.querySelectorAll('.sp-result__step');
        steps.forEach(function (el) { el.hidden = true; });

        var targetId = 'sp-result-' + stepName;
        var target = document.getElementById(targetId);
        if (target) target.hidden = false;

        window.scrollTo(0, 0);

        // Update SP header back button visibility
        StarterPackResult.StepRenderer.updateHeaderBack(stepName);
      },

      updateHeaderBack: function (stepName) {
        var headerBack = document.getElementById('sp-header-back-btn');
        if (!headerBack) return;

        if (stepName === 'frequency') {
          headerBack.hidden = false;
          headerBack.onclick = function () {
            if (StarterPackResult.State.hasAddons) {
              StarterPackResult.State.currentStep = 'addons';
              StarterPackResult.StepRenderer.showStep('addons');
            } else {
              StarterPackResult.State.currentStep = 'reveal';
              StarterPackResult.StepRenderer.showStep('reveal');
            }
          };
        } else if (stepName === 'addons') {
          headerBack.hidden = false;
          headerBack.onclick = function () {
            StarterPackResult.State.currentStep = 'reveal';
            StarterPackResult.StepRenderer.showStep('reveal');
          };
        } else {
          headerBack.hidden = true;
          headerBack.onclick = null;
        }
      },

      renderReveal: function (data) {


        var subtitleEl = document.getElementById('sp-result-pack-subtitle');
        if (subtitleEl) {
          subtitleEl.textContent = 'Daily vitamin sachets, personalised for you.';
        }

        var listEl = document.getElementById('sp-result-product-list');
        if (!listEl) return;
        listEl.innerHTML = '';

        (data.products || []).forEach(function (product, idx) {
          var card = StarterPackResult.StepRenderer.createProductCard(product, idx);
          listEl.appendChild(card);
        });

        var cta = document.getElementById('sp-result-reveal-cta');
        if (cta) {
          cta.addEventListener('click', function () {
            var addons = data.optional_addons || [];
            if (addons.length > StarterPackResult.Config.MAX_ADDONS) {
              addons = addons.slice(0, StarterPackResult.Config.MAX_ADDONS);
            }
            StarterPackResult.State.hasAddons = addons.length > 0;
            if (addons.length > 0) {
              StarterPackResult.State.currentStep = 'addons';
              StarterPackResult.StepRenderer.showStep('addons');
              StarterPackResult.StepRenderer.renderAddons(addons);
            } else {
              StarterPackResult.State.currentStep = 'frequency';
              StarterPackResult.StepRenderer.showStep('frequency');
              StarterPackResult.StepRenderer.renderFrequency();
            }
          });
        }
      },

      createProductCard: function (product, index) {
        var card = document.createElement('div');
        card.className = 'sp-result__product-card';
        card.setAttribute('data-variant-id', product.variant_id);

        var productData = (window.personalised && window.personalised.productData) || {};
        var pd = productData[product.product_id];
        var isBulk = pd && pd.metafields && pd.metafields.personalised_bulk;

        // Clickable header row
        var header = document.createElement('div');
        header.className = 'sp-result__product-card-header';

        var imgWrap = document.createElement('div');
        imgWrap.className = 'sp-result__product-card-image-wrap';
        var img = document.createElement('img');
        img.className = 'sp-result__product-card-image' + (isBulk ? ' sp-result__product-card-image--bulk' : '');
        img.src = product.image_url || '';
        img.alt = product.title || '';
        img.loading = 'lazy';
        imgWrap.appendChild(img);
        header.appendChild(imgWrap);

        var body = document.createElement('div');
        body.className = 'sp-result__product-card-body';

        var title = document.createElement('h3');
        title.className = 'sp-result__product-card-title';
        title.textContent = product.title || '';
        body.appendChild(title);

        var reason = document.createElement('p');
        reason.className = 'sp-result__product-card-reason';
        reason.textContent = product.reason || '';
        body.appendChild(reason);

        // "more" text toggle (inside body, below reason)
        var moreBtn = document.createElement('span');
        moreBtn.className = 'sp-result__product-card-more';
        moreBtn.textContent = 'more +';
        body.appendChild(moreBtn);

        header.appendChild(body);

        card.appendChild(header);

        // Action column: price-per-day + remove icon
        var actionCol = document.createElement('div');
        actionCol.className = 'sp-result__product-card-action';

        // Price per day
        if (pd && pd.variants && pd.variants.length > 0) {
          var matchedVariant = null;
          for (var vi = 0; vi < pd.variants.length; vi++) {
            if (String(pd.variants[vi].id) === String(product.variant_id)) {
              matchedVariant = pd.variants[vi];
              break;
            }
          }
          if (!matchedVariant) matchedVariant = pd.variants[0];
          if (matchedVariant && matchedVariant.price) {
            var perDay = (parseFloat(matchedVariant.price) / 100 / 30);
            var pricePerDayEl = document.createElement('span');
            pricePerDayEl.className = 'sp-result__price-per-day';
            pricePerDayEl.textContent = '$' + perDay.toFixed(2) + '/day';
            actionCol.appendChild(pricePerDayEl);
          }
        }

        var removeBtn = document.createElement('button');
        removeBtn.className = 'sp-result__product-card-remove';
        removeBtn.type = 'button';
        removeBtn.setAttribute('aria-label', 'Remove ' + (product.title || 'product'));
        removeBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>';
        actionCol.appendChild(removeBtn);
        card.appendChild(actionCol);

        // Detail panel (hidden by default)
        var detail = StarterPackResult.StepRenderer.createDetailPanel(product, pd);
        card.appendChild(detail);

        // Toggle detail on header click
        header.addEventListener('click', function () {
          var isOpen = card.classList.contains('sp-result__product-card--open');
          card.classList.toggle('sp-result__product-card--open');
          detail.hidden = isOpen;
          moreBtn.textContent = isOpen ? 'more +' : 'less \u2212';
        });

        removeBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          var removed = StarterPackResult.BundlePopulator.removeProduct(product.variant_id);
          if (removed) {
            card.remove();
          }
        });

        return card;
      },

      createDetailPanel: function (product, pd) {
        var panel = document.createElement('div');
        panel.className = 'sp-result__detail-panel';
        panel.hidden = true;

        var esc = StarterPackResult.Helpers.esc;

        // Product description
        if (product.recoBody) {
          var descP = document.createElement('p');
          descP.className = 'sp-result__detail-desc';
          descP.textContent = product.recoBody;
          panel.appendChild(descP);
        }

        // Quality badges
        if (product.qualityIcons && product.qualityIcons.length > 0) {
          var badgesWrap = document.createElement('div');
          badgesWrap.className = 'sp-result__detail-badges';
          product.qualityIcons.forEach(function (icon) {
            if (icon.handle === 'scientific_research' || icon.handle === 'traditionally_used') return;
            var badge = document.createElement('span');
            badge.className = 'sp-result__detail-badge';
            var badgeImg = document.createElement('img');
            badgeImg.src = icon.url || '';
            badgeImg.alt = icon.label || '';
            badgeImg.width = 18;
            badgeImg.height = 18;
            badge.appendChild(badgeImg);
            var badgeLabel = document.createElement('span');
            badgeLabel.textContent = icon.label || '';
            badge.appendChild(badgeLabel);
            badgesWrap.appendChild(badge);
          });
          panel.appendChild(badgesWrap);
        }

        // Dosage
        var dosage = product.recoDosage || (pd && pd.metafields && pd.metafields.reco_dosage) || '';
        if (dosage) {
          var doseSection = document.createElement('div');
          doseSection.className = 'sp-result__detail-section';
          doseSection.innerHTML = '<strong>Daily Dose</strong><p>' + esc(dosage) + '</p>';
          panel.appendChild(doseSection);
        }

        // Ingredients
        if (product.recoIngredients) {
          var ingSection = document.createElement('div');
          ingSection.className = 'sp-result__detail-section';
          ingSection.innerHTML = '<strong>Ingredients</strong><p>' + esc(product.recoIngredients) + '</p>';
          panel.appendChild(ingSection);
        }

        // Allergens
        if (product.recoAllergens) {
          var allergenSection = document.createElement('div');
          allergenSection.className = 'sp-result__detail-section';
          allergenSection.innerHTML = '<strong>Allergens</strong><p>' + esc(product.recoAllergens) + '</p>';
          panel.appendChild(allergenSection);
        }

        // Warnings
        if (product.recoDisclaimer) {
          var warnSection = document.createElement('div');
          warnSection.className = 'sp-result__detail-section';
          warnSection.innerHTML = '<strong>Warnings</strong><p>' + esc(product.recoDisclaimer) + '</p>';
          panel.appendChild(warnSection);
        }

        // Label image button (opens modal overlay)
        if (product.labelImage && product.labelImage.url) {
          var labelBtn = document.createElement('button');
          labelBtn.type = 'button';
          labelBtn.className = 'sp-result__detail-label-link';
          labelBtn.textContent = 'View full product label';
          labelBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            StarterPackResult.StepRenderer.showLabelModal(product.labelImage.url, product.title || '');
          });
          panel.appendChild(labelBtn);
        }

        return panel;
      },

      renderAddons: function (addons) {
        var listEl = document.getElementById('sp-result-addon-list');
        if (!listEl) return;
        listEl.innerHTML = '';

        addons.forEach(function (addon) {
          var card = document.createElement('div');
          card.className = 'sp-result__addon-card';
          card.setAttribute('data-variant-id', addon.variant_id);

          var img = document.createElement('img');
          img.className = 'sp-result__addon-card-image';
          img.src = addon.image_url || '';
          img.alt = addon.title || '';
          img.loading = 'lazy';
          card.appendChild(img);

          var body = document.createElement('div');
          body.className = 'sp-result__addon-card-body';

          var title = document.createElement('h3');
          title.className = 'sp-result__addon-card-title';
          title.textContent = addon.title || '';
          body.appendChild(title);

          var reason = document.createElement('p');
          reason.className = 'sp-result__addon-card-reason';
          reason.textContent = addon.reason || '';
          body.appendChild(reason);

          // "more" text toggle
          var moreBtn = document.createElement('span');
          moreBtn.className = 'sp-result__addon-card-more';
          moreBtn.textContent = 'more +';
          body.appendChild(moreBtn);

          card.appendChild(body);

          // Detail panel (hidden)
          var productData = (window.personalised && window.personalised.productData) || {};
          var pd = productData[addon.product_id];
          var detail = StarterPackResult.StepRenderer.createDetailPanel(addon, pd);
          card.appendChild(detail);

          // Toggle detail on body click
          (function (mb) {
            body.addEventListener('click', function (e) {
              var isOpen = card.classList.contains('sp-result__addon-card--open');
              card.classList.toggle('sp-result__addon-card--open');
              detail.hidden = isOpen;
              mb.textContent = isOpen ? 'more +' : 'less \u2212';
            });
          })(moreBtn);

          // Price + action column
          var actionCol = document.createElement('div');
          actionCol.className = 'sp-result__addon-card-action';
          actionCol.style.cssText = 'display: flex; flex-direction: column; align-items: flex-end; gap: 4px;';

          if (pd && pd.variants && pd.variants.length > 0) {
            var addonVariant = null;
            for (var vi = 0; vi < pd.variants.length; vi++) {
              if (String(pd.variants[vi].id) === String(addon.variant_id)) {
                addonVariant = pd.variants[vi];
                break;
              }
            }
            if (!addonVariant) addonVariant = pd.variants[0];
            if (addonVariant && addonVariant.price) {
              var addonPerDay = (parseFloat(addonVariant.price) / 100 / 30);
              var priceEl = document.createElement('span');
              priceEl.className = 'sp-result__price-per-day';
              priceEl.textContent = '$' + addonPerDay.toFixed(2) + '/day';
              actionCol.appendChild(priceEl);
            }
          }

          var addBtn = document.createElement('button');
          addBtn.className = 'sp-result__addon-btn';
          addBtn.type = 'button';
          addBtn.textContent = '+';
          actionCol.appendChild(addBtn);
          card.appendChild(actionCol);

          addBtn.addEventListener('click', function () {
            var isAdded = card.classList.contains('sp-result__addon-card--added');

            if (!isAdded) {
              // Check disclaimer before adding
              var check = StarterPackResult.Disclaimer.checkBeforeAddon();
              if (check.needed && check.promise) {
                check.promise.then(function (confirmed) {
                  if (confirmed) {
                    StarterPackResult.BundlePopulator.addAddon(addon);
                    card.classList.add('sp-result__addon-card--added');
                    addBtn.classList.add('sp-result__addon-btn--added');
                    addBtn.textContent = '\u2713';
                    // Toggle skip button visibility
                    var skipBtn = document.getElementById('sp-result-addon-skip');
                    if (skipBtn) {
                      skipBtn.style.display = StarterPackResult.State.addedAddons.length > 0 ? 'none' : '';
                    }
                  }
                });
                return;
              }
              StarterPackResult.BundlePopulator.addAddon(addon);
              card.classList.add('sp-result__addon-card--added');
              addBtn.classList.add('sp-result__addon-btn--added');
              addBtn.textContent = '\u2713';
            } else {
              StarterPackResult.BundlePopulator.removeProduct(addon.variant_id);
              // Remove from addedAddons state
              var addedArr = StarterPackResult.State.addedAddons;
              for (var ri = addedArr.length - 1; ri >= 0; ri--) {
                if (String(addedArr[ri].variant_id) === String(addon.variant_id)) {
                  addedArr.splice(ri, 1);
                  break;
                }
              }
              card.classList.remove('sp-result__addon-card--added');
              addBtn.classList.remove('sp-result__addon-btn--added');
              addBtn.textContent = '+';
            }

            // Toggle skip button visibility
            var skipBtn = document.getElementById('sp-result-addon-skip');
            if (skipBtn) {
              var hasAddons = StarterPackResult.State.addedAddons.length > 0;
              skipBtn.style.display = hasAddons ? 'none' : '';
            }
          });

          listEl.appendChild(card);
        });

        var goToFrequency = function () {
          StarterPackResult.State.currentStep = 'frequency';
          StarterPackResult.StepRenderer.showStep('frequency');
          StarterPackResult.StepRenderer.renderFrequency();
        };

        var continueBtn = document.getElementById('sp-result-addon-continue');
        var skipBtn = document.getElementById('sp-result-addon-skip');
        if (continueBtn) continueBtn.addEventListener('click', goToFrequency);
        if (skipBtn) skipBtn.addEventListener('click', goToFrequency);
      },

      renderFrequency: function () {
        var optionsEl = document.getElementById('sp-result-freq-options');
        if (!optionsEl) return;
        optionsEl.innerHTML = '';

        var items = window.personalised.bundle.getItems();
        var baseMonthlyTotal = StarterPackResult.calculateBaseMonthlyPrice(items);

        Object.keys(StarterPackResult.Config.FREQUENCY_OPTIONS).forEach(function (freq) {
          var option = StarterPackResult.Config.FREQUENCY_OPTIONS[freq];
          var card = StarterPackResult.StepRenderer.createFrequencyCard(freq, option, baseMonthlyTotal, items.length);
          optionsEl.appendChild(card);
        });

        // Render checkout summary inline (merged step)
        StarterPackResult.StepRenderer.renderCheckout();
      },

      createFrequencyCard: function (freq, option, baseMonthlyTotal, itemCount) {
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'sp-result__freq-card';
        if (StarterPackResult.State.selectedFrequency && freq === StarterPackResult.State.selectedFrequency) {
          card.classList.add('sp-result__freq-card--selected');
        }

        var orderTotal = baseMonthlyTotal * option.multiplier * (1 - option.discount);
        var qualifiesFreeShipping = option.freeShipping && orderTotal > StarterPackResult.Config.FREE_SHIPPING_THRESHOLD;
        var perDay = orderTotal / (parseInt(freq, 10));

        if (option.badge) {
          var badge = document.createElement('span');
          badge.className = 'sp-result__freq-card-badge';
          badge.textContent = option.badge;
          card.appendChild(badge);
        }

        var left = document.createElement('div');
        left.className = 'sp-result__freq-card-left';

        var labelEl = document.createElement('span');
        labelEl.className = 'sp-result__freq-card-label';
        labelEl.textContent = option.label;
        left.appendChild(labelEl);

        var descEl = document.createElement('span');
        descEl.className = 'sp-result__freq-card-desc';
        descEl.textContent = 'Delivery every ' + freq + ' days' +
          (qualifiesFreeShipping ? ' · Free shipping' : '');
        left.appendChild(descEl);

        card.appendChild(left);

        var right = document.createElement('div');
        right.className = 'sp-result__freq-card-right';

        var priceEl = document.createElement('span');
        priceEl.className = 'sp-result__freq-card-price';
        priceEl.textContent = '$' + orderTotal.toFixed(2);
        right.appendChild(priceEl);

        if (option.discount > 0) {
          var savingsEl = document.createElement('span');
          savingsEl.className = 'sp-result__freq-card-savings';
          savingsEl.textContent = 'Save ' + Math.round(option.discount * 100) + '%';
          right.appendChild(savingsEl);
        }

        if (!option.freeShipping) {
          var shipNote = document.createElement('span');
          shipNote.className = 'sp-result__freq-card-ship';
          shipNote.textContent = '+ shipping';
          right.appendChild(shipNote);
        } else if (!qualifiesFreeShipping) {
          var shipNote2 = document.createElement('span');
          shipNote2.className = 'sp-result__freq-card-ship';
          shipNote2.textContent = '+ shipping';
          right.appendChild(shipNote2);
        }

        card.appendChild(right);

        card.addEventListener('click', function () {
          StarterPackResult.FrequencyManager.select(freq);
          document.querySelectorAll('.sp-result__freq-card').forEach(function (el) {
            el.classList.remove('sp-result__freq-card--selected');
          });
          card.classList.add('sp-result__freq-card--selected');
          StarterPackResult.StepRenderer.renderCheckout();
        });

        return card;
      },

      renderCheckout: function () {
        var summaryEl = document.getElementById('sp-result-summary');
        if (!summaryEl) return;

        var freq = StarterPackResult.State.selectedFrequency;
        var checkoutCta = document.querySelector('.sp-result__checkout-cta');

        // If no frequency selected, hide summary and checkout CTA
        if (!freq) {
          summaryEl.style.display = 'none';
          if (checkoutCta) checkoutCta.style.display = 'none';
          return;
        }

        // Show summary and checkout CTA
        summaryEl.style.display = '';
        if (checkoutCta) checkoutCta.style.display = '';

        var checkoutBtn = document.getElementById('sp-result-checkout-btn');
        if (checkoutBtn) {
          checkoutBtn.disabled = false;
          checkoutBtn.style.opacity = '1';
        }

        var items = window.personalised.bundle.getItems();
        var option = StarterPackResult.Config.FREQUENCY_OPTIONS[freq];
        var baseMonthlyTotal = StarterPackResult.calculateBaseMonthlyPrice(items);
        var orderTotal = baseMonthlyTotal * option.multiplier * (1 - option.discount);
        var perDay = orderTotal / parseInt(freq, 10);
        var productData = (window.personalised && window.personalised.productData) || {};

        summaryEl.innerHTML = '';

        // Heading
        var heading = document.createElement('h3');
        heading.className = 'sp-result__summary-heading';
        heading.textContent = 'Pack Summary';
        summaryEl.appendChild(heading);

        // Summary box
        var box = document.createElement('div');
        box.className = 'sp-result__summary-box';

        // Pack line with accordion
        var packRow = document.createElement('div');
        packRow.className = 'sp-result__summary-item';
        packRow.style.cssText = 'cursor: pointer; user-select: none;';

        var packNameEl = document.createElement('span');
        packNameEl.className = 'sp-result__summary-item-name';
        packNameEl.innerHTML = '<span style="margin-right:6px;">&#9654;</span>Pack (' + items.length + ' vitamin' + (items.length !== 1 ? 's' : '') + ' &times; ' + option.multiplier + ' month' + (option.multiplier !== 1 ? 's' : '') + ')';
        packRow.appendChild(packNameEl);

        var subtotalPrice = baseMonthlyTotal * option.multiplier;
        var packPriceEl = document.createElement('span');
        packPriceEl.className = 'sp-result__summary-item-price';
        packPriceEl.innerHTML = '<span style="text-decoration:line-through;color:rgba(39,38,38,0.4);font-weight:400;">$' + subtotalPrice.toFixed(2) + '</span>';
        packRow.appendChild(packPriceEl);

        box.appendChild(packRow);

        // Accordion product list (hidden by default)
        var productListWrap = document.createElement('div');
        productListWrap.className = 'sp-result__summary-products';
        productListWrap.style.display = StarterPackResult.State.summaryListOpen ? 'block' : 'none';

        items.forEach(function (item) {
          var pd = productData[item.productId];
          var itemRow = document.createElement('div');
          itemRow.className = 'sp-result__summary-product-row';

          var itemName = document.createElement('span');
          itemName.className = 'sp-result__summary-product-name';

          // Variant title as dose (e.g. "1 capsule daily")
          var variantTitle = '';
          var variantPrice = 0;
          if (pd && pd.variants) {
            for (var vi = 0; vi < pd.variants.length; vi++) {
              if (String(pd.variants[vi].id) === String(item.variantId)) {
                variantTitle = pd.variants[vi].title || '';
                variantPrice = pd.variants[vi].price || 0;
                break;
              }
            }
          }
          var nameText = (pd && pd.title) || 'Product';
          if (variantTitle) nameText += ' (' + variantTitle + ')';
          itemName.textContent = nameText;
          itemRow.appendChild(itemName);

          // Per-product price
          if (variantPrice) {
            var priceEl = document.createElement('span');
            priceEl.className = 'sp-result__summary-product-price';
            priceEl.textContent = '$' + (variantPrice / 100).toFixed(2);
            itemRow.appendChild(priceEl);
          }

          // Remove button
          var removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'sp-result__summary-product-remove';
          removeBtn.setAttribute('aria-label', 'Remove ' + ((pd && pd.title) || 'product'));
          removeBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
          removeBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            var removed = StarterPackResult.BundlePopulator.removeProduct(item.variantId);
            if (removed) {
              // Preserve open state and re-render
              StarterPackResult.State.summaryListOpen = true;
              StarterPackResult.StepRenderer.renderCheckout();
            }
          });
          itemRow.appendChild(removeBtn);

          productListWrap.appendChild(itemRow);
        });

        box.appendChild(productListWrap);

        // Set initial arrow state
        if (StarterPackResult.State.summaryListOpen) {
          packNameEl.querySelector('span').innerHTML = '&#9660;';
        }

        // Toggle accordion
        packRow.addEventListener('click', function () {
          var isOpen = productListWrap.style.display !== 'none';
          productListWrap.style.display = isOpen ? 'none' : 'block';
          StarterPackResult.State.summaryListOpen = !isOpen;
          packNameEl.querySelector('span').innerHTML = isOpen ? '&#9654;' : '&#9660;';
        });

        // Frequency discount
        if (option.discount > 0) {
          var discountAmount = baseMonthlyTotal * option.multiplier * option.discount;
          var discRow = document.createElement('div');
          discRow.className = 'sp-result__summary-item';
          discRow.innerHTML = '<span class="sp-result__summary-item-name" style="color: var(--sp-color-sage);">' + Math.round(option.discount * 100) + '% multi-month discount</span>' +
            '<span class="sp-result__summary-item-price" style="color: var(--sp-color-sage);">-$' + discountAmount.toFixed(2) + '</span>';
          box.appendChild(discRow);
        }

        // GETSTARTED discount (25%)
        var welcomeDiscount = orderTotal * 0.25;
        var welcomeRow = document.createElement('div');
        welcomeRow.className = 'sp-result__summary-item';
        welcomeRow.innerHTML = '<span class="sp-result__summary-item-name" style="color: var(--sp-color-watermelon);">25% GETSTARTED discount</span>' +
          '<span class="sp-result__summary-item-price" style="color: var(--sp-color-watermelon);">-$' + welcomeDiscount.toFixed(2) + '</span>';
        box.appendChild(welcomeRow);

        // Shipping
        var qualifiesFreeShip = option.freeShipping && orderTotal > StarterPackResult.Config.FREE_SHIPPING_THRESHOLD;
        var shipRow = document.createElement('div');
        shipRow.className = 'sp-result__summary-item';
        shipRow.innerHTML = '<span class="sp-result__summary-item-name">Shipping</span>' +
          '<span class="sp-result__summary-item-price">' + (qualifiesFreeShip ? 'Free' : 'Calculated at checkout') + '</span>';
        box.appendChild(shipRow);

        // Divider
        var divider2 = document.createElement('div');
        divider2.style.cssText = 'border-top: 1px solid rgba(39,38,38,0.1); margin: 10px 0;';
        box.appendChild(divider2);

        // Total
        var finalTotal = orderTotal - welcomeDiscount;
        var totalRow = document.createElement('div');
        totalRow.className = 'sp-result__summary-total';
        totalRow.innerHTML = '<span class="sp-result__summary-total-label">Total</span>' +
          '<div class="sp-result__summary-total-right">' +
            '<span class="sp-result__summary-total-price">$' + finalTotal.toFixed(2) + '</span>' +
            '<span class="sp-result__summary-per-day">$' + (finalTotal / parseInt(freq, 10)).toFixed(2) + '/day</span>' +
          '</div>';
        box.appendChild(totalRow);

        // Bottom note
        var noteEl = document.createElement('p');
        noteEl.className = 'sp-result__summary-note';
        noteEl.textContent = 'Vitable is a subscription - Adjust or cancel anytime';
        box.appendChild(noteEl);

        summaryEl.appendChild(box);

        // Bind checkout button (re-bind on each render)
        var checkoutBtn2 = document.getElementById('sp-result-checkout-btn');
        if (checkoutBtn2) {
          checkoutBtn2.onclick = function () {
            StarterPackResult.CheckoutManager.checkout();
          };
        }
      },

      showLabelModal: function (imageUrl, productTitle) {
        var overlay = document.createElement('div');
        overlay.className = 'sp-result__modal sp-result__label-modal';
        overlay.innerHTML =
          '<div class="sp-result__modal-overlay"></div>' +
          '<div class="sp-result__label-modal-content">' +
            '<img src="' + StarterPackResult.Helpers.esc(imageUrl) + '" alt="Supplement facts for ' + StarterPackResult.Helpers.esc(productTitle) + '" class="sp-result__label-modal-image" />' +
            '<button class="sp-result__label-modal-close" type="button" aria-label="Close">&times;</button>' +
          '</div>';

        document.getElementById('sp-result').appendChild(overlay);

        var close = function () {
          if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
          document.removeEventListener('keydown', escHandler);
        };

        overlay.querySelector('.sp-result__modal-overlay').addEventListener('click', close);
        overlay.querySelector('.sp-result__label-modal-close').addEventListener('click', close);

        var escHandler = function (e) {
          if (e.key === 'Escape') close();
        };
        document.addEventListener('keydown', escHandler);
      },

      showLastItemModal: function () {
        var modal = document.getElementById('sp-result-modal');
        var titleEl = document.getElementById('sp-result-modal-title');
        var textEl = document.getElementById('sp-result-modal-text');
        var confirmBtn = document.getElementById('sp-result-modal-confirm');
        var cancelBtn = document.getElementById('sp-result-modal-cancel');

        if (titleEl) titleEl.textContent = "Can't remove last item";
        if (textEl) textEl.textContent = "Your pack needs at least one product. Want to retake the quiz for different recommendations?";
        if (confirmBtn) {
          confirmBtn.textContent = 'Retake Quiz';
          confirmBtn.onclick = function () {
            window.location.href = '/pages/sp-quiz';
          };
        }
        if (cancelBtn) {
          cancelBtn.onclick = function () {
            if (modal) modal.hidden = true;
          };
        }
        if (modal) {
          // Clicking overlay closes modal
          var overlay = modal.querySelector('.sp-result__modal-overlay');
          if (overlay) {
            overlay.onclick = function () {
              modal.hidden = true;
            };
          }
          modal.hidden = false;
        }
      }
    },

    // ─── FREQUENCY MANAGER ──────────────────────────────────────────────────
    FrequencyManager: {
      select: function (frequency) {
        var validFreqs = ['30', '60', '90'];
        if (validFreqs.indexOf(frequency) === -1) {
          console.warn('[SP Result] Invalid frequency:', frequency);
          return;
        }

        StarterPackResult.State.selectedFrequency = frequency;

        // Update bundle frequency (updates all selling plan IDs on items)
        window.personalised.bundle.setFrequency(frequency);

        // Explicitly schedule sync for certainty (setFrequency may or may not auto-trigger)
        if (window.personalised.cartSync && typeof window.personalised.cartSync.scheduleSync === 'function') {
          window.personalised.cartSync.scheduleSync('starter-pack-frequency-changed');
        }

        StarterPackResult.Analytics.frequencySelected(frequency);
      }
    },

    // ─── CHECKOUT MANAGER ───────────────────────────────────────────────────
    CheckoutManager: {
      checkout: function () {
        if (StarterPackResult.State.checkoutInProgress) return;

        // Guard: require frequency selection
        if (!StarterPackResult.State.selectedFrequency) {
          // Scroll to frequency options to prompt user
          var freqEl = document.getElementById('sp-result-freq-options');
          if (freqEl) freqEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        StarterPackResult.State.checkoutInProgress = true;

        var checkoutBtn = document.getElementById('sp-result-checkout-btn');
        if (checkoutBtn) {
          checkoutBtn.disabled = true;
          checkoutBtn.textContent = 'Updating your pack...';
        }

        var p = window.personalised;
        var cartSync = p && p.cartSync;
        var cartSyncState = p && p.cartSyncState;

        var isSyncing = (cartSync && cartSync._syncInProgress) ||
                        (cartSyncState && cartSyncState.syncPending);

        if (isSyncing) {
          StarterPackResult.CheckoutManager.waitForSync(function () {
            StarterPackResult.CheckoutManager.performCheckout();
          });
        } else {
          // Ensure cart is current, then proceed
          if (cartSync && typeof cartSync.scheduleSync === 'function') {
            cartSync.scheduleSync('starter-pack-pre-checkout');
          }
          StarterPackResult.CheckoutManager.waitForSync(function () {
            StarterPackResult.CheckoutManager.performCheckout();
          });
        }
      },

      waitForSync: function (callback) {
        var p = window.personalised;
        var cartSync = p && p.cartSync;
        var cartSyncState = p && p.cartSyncState;
        var eventBus = p && p.eventBus;

        var isSyncing = (cartSync && cartSync._syncInProgress) ||
                        (cartSyncState && cartSyncState.syncPending);

        if (!isSyncing || !eventBus) {
          callback();
          return;
        }

        var timeout;
        var handler = function () {
          clearTimeout(timeout);
          if (eventBus) {
            eventBus.off('syncCompleted', handler);
            eventBus.off('syncFailed', handler);
          }
          callback();
        };

        eventBus.on('syncCompleted', handler);
        eventBus.on('syncFailed', handler);

        // Safety: proceed after 10s regardless
        timeout = setTimeout(function () {
          if (eventBus) {
            eventBus.off('syncCompleted', handler);
            eventBus.off('syncFailed', handler);
          }
          callback();
        }, 10000);
      },

      performCheckout: function () {
        StarterPackResult.Analytics.checkoutInitiated();

        // Apply discount code if cart doesn't already have one
        StarterPackResult.CheckoutManager.cartHasDiscount().then(function (hasDiscount) {
          if (!hasDiscount && StarterPackResult.Config.discountCode) {
            // Redirect through Shopify's /discount/ route which applies code then goes to checkout
            window.location.href = '/discount/' +
              encodeURIComponent(StarterPackResult.Config.discountCode) +
              '?redirect=/checkout';
            return;
          }

          // Standard Shopify cart checkout -- form POST to /cart with checkout hidden input
          var form = document.createElement('form');
          form.method = 'POST';
          form.action = '/cart';
          form.style.display = 'none';

          var checkoutInput = document.createElement('input');
          checkoutInput.type = 'hidden';
          checkoutInput.name = 'checkout';
          checkoutInput.value = '';
          form.appendChild(checkoutInput);

          document.body.appendChild(form);
          form.submit();
        });
      },

      /**
       * Check whether the cart already has a discount code applied.
       * Returns a Promise that resolves to true if a code is present.
       */
      cartHasDiscount: function () {
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            return !!(cart.discount_codes && cart.discount_codes.length > 0);
          })
          .catch(function () { return false; });
      }
    },

    // ─── ANALYTICS ──────────────────────────────────────────────────────────
    Analytics: {
      packRevealed: function (data) {

        var items = window.personalised.bundle && window.personalised.bundle.getItems();
        var value = items ? StarterPackResult.calculateBaseMonthlyPrice(items) : 0;

        if (typeof gtag === 'function') {
          gtag('event', 'sp_pack_revealed', {
            product_count: (data.products || []).length,
            source: 'starter_pack'
          });
        }
        if (window.ttq && typeof window.ttq.track === 'function') {
          window.ttq.track('ViewContent', {
            content_type: 'pack_result',
            content_id: 'starter_pack',
            value: value,
            currency: 'AUD'
          });
        }
      },

      frequencySelected: function (frequency) {
        if (typeof gtag === 'function') {
          gtag('event', 'sp_frequency_selected', {
            frequency: frequency,
            source: 'starter_pack'
          });
        }
      },

      addonAdded: function (product) {
        if (typeof gtag === 'function') {
          gtag('event', 'sp_addon_added', {
            product_handle: product.handle || '',
            source: 'starter_pack'
          });
        }
      },

      productRemoved: function (variantId) {
        if (typeof gtag === 'function') {
          gtag('event', 'sp_product_removed', {
            variant_id: String(variantId),
            source: 'starter_pack'
          });
        }
      },

      checkoutInitiated: function () {
        var p = window.personalised;
        var items = (p && p.bundle && p.bundle.getItems()) || [];
        var freq = StarterPackResult.State.selectedFrequency;
        var value = StarterPackResult.calculateBaseMonthlyPrice(items);

        if (typeof gtag === 'function') {
          gtag('event', 'sp_checkout_initiated', {
            product_count: items.length,
            frequency: freq,
            source: 'starter_pack'
          });
        }
        if (window.ttq && typeof window.ttq.track === 'function') {
          window.ttq.track('InitiateCheckout', {
            content_type: 'pack_checkout',
            content_id: 'starter_pack',
            value: value,
            currency: 'AUD'
          });
        }
        if (typeof fbq === 'function') {
          fbq('track', 'InitiateCheckout', {
            content_type: 'product',
            num_items: items.length,
            currency: 'AUD'
          });
        }
      }
    },

    // ─── DISCLAIMER ─────────────────────────────────────────────────────────
    Disclaimer: {
      /**
       * Sync pregnancy/allergy/name values from API response to localStorage.
       * Mirrors recommendation.js syncQuizValuesFromRecommendation() pattern.
       */
      syncSafetyValues: function (data) {
        try {
          var pregnancy = data.pregnancy || '';
          var allergy = data.allergy || '';
          var firstName = data.raw_response && data.raw_response.firstName;

          if (pregnancy && pregnancy !== 'no' && pregnancy !== 'none') {
            localStorage.setItem('vitable_pregnancy', pregnancy);
          } else {
            localStorage.removeItem('vitable_pregnancy');
          }

          if (allergy && allergy !== 'none') {
            localStorage.setItem('vitable_allergy', allergy);
          } else {
            localStorage.removeItem('vitable_allergy');
          }

          if (firstName && firstName.trim() !== '') {
            localStorage.setItem('vitable_name_on_pack', firstName.trim());
          }
        } catch (e) {
          console.warn('[SP Disclaimer] localStorage unavailable:', e);
        }
      },

      /**
       * Show page-load disclaimer modal if user has relevant pregnancy/allergy flags.
       * Mirrors recommendation.js checkAndShowDisclaimerModal() behaviour.
       */
      showPageLoadModal: function (data) {
        var allergy = data.allergy || '';
        var pregnancy = data.pregnancy || '';

        var hasRelevantAllergy = allergy && !(['no', 'none', ''].indexOf(allergy.toLowerCase().trim()) > -1);
        var hasRelevantPregnancy = pregnancy && !(['no', 'none', ''].indexOf(pregnancy.toLowerCase().trim()) > -1);

        if (!hasRelevantAllergy && !hasRelevantPregnancy) return;

        // Sanitize values for safe HTML insertion (defense-in-depth)
        function esc(str) {
          var el = document.createElement('span');
          el.textContent = str;
          return el.innerHTML;
        }

        var messages = [];

        if (hasRelevantAllergy) {
          messages.push(
            '<p>You mentioned that you are allergic to <strong>' + esc(allergy) + '</strong>. ' +
            'While the products recommended to you contain no added <strong>' + esc(allergy) + '</strong>, ' +
            'we can\u2019t guarantee they don\u2019t contain small traces of this allergen. ' +
            'Read each product label carefully if you consider adding other supplements to your pack.</p>'
          );
        }

        if (hasRelevantPregnancy) {
          messages.push(
            '<p>You mentioned that you are <strong>' + esc(pregnancy) + '</strong>. ' +
            'Some vitamins and/or supplements may not be right for you. Read the label and any warnings before purchase. ' +
            'We recommend consulting your health professional before starting a vitamin and supplement routine.' +
            '<br />Reach out to our lovely team if you consider adding other supplements to your pack, ' +
            'so they can check for you, at <a href="mailto:hello@vitable.com.au">hello@vitable.com.au</a></p>'
          );
        }

        // Build modal HTML
        var overlay = document.createElement('div');
        overlay.id = 'sp-result-disclaimer-modal';
        overlay.className = 'sp-result__modal';
        overlay.innerHTML =
          '<div class="sp-result__modal-overlay"></div>' +
          '<div class="sp-result__modal-content sp-result__disclaimer-content">' +
            '<div class="sp-result__disclaimer-body">' + messages.join('') + '</div>' +
            '<div class="sp-result__modal-actions">' +
              '<button class="sp-result__cta-btn" id="sp-result-disclaimer-ok" type="button">I understand</button>' +
              '<a class="sp-result__skip-btn" id="sp-result-disclaimer-cancel" href="/pages/sp-quiz">Cancel and retake quiz</a>' +
            '</div>' +
          '</div>';

        document.getElementById('sp-result').appendChild(overlay);

        document.getElementById('sp-result-disclaimer-ok').addEventListener('click', function () {
          overlay.hidden = true;
          overlay.parentNode.removeChild(overlay);
        });

        document.getElementById('sp-result-disclaimer-cancel').addEventListener('click', function (e) {
          e.preventDefault();
          window.location.href = '/pages/sp-quiz';
        });
      },

      /**
       * Check if user has relevant allergy/pregnancy flags requiring a disclaimer
       * before adding an addon product. Returns Promise<boolean>.
       * Reuses personalised-disclaimer-modal.js if available, falls back to proceed.
       */
      checkBeforeAddon: function () {
        var allergy = null;
        var pregnancy = null;
        try {
          allergy = localStorage.getItem('vitable_allergy');
          pregnancy = localStorage.getItem('vitable_pregnancy');
        } catch (e) { /* localStorage unavailable */ }

        var hasRelevantAllergy = allergy && ['no', 'none', ''].indexOf(allergy.toLowerCase().trim()) === -1;
        var hasRelevantPregnancy = pregnancy && ['no', 'none', ''].indexOf(pregnancy.toLowerCase().trim()) === -1;

        if (!hasRelevantAllergy && !hasRelevantPregnancy) {
          return { needed: false };
        }

        var esc = StarterPackResult.Helpers.esc;
        var messages = [];
        if (hasRelevantAllergy) {
          messages.push('<p>This product may contain allergens which are not suitable for you. Ensure you read each product label carefully if you consider adding this supplement to your pack.</p>');
        }
        if (hasRelevantPregnancy) {
          messages.push('<p>Based on your quiz responses, this product might not be suitable for you. Are you sure you want to add this product to your pack? Talk to your doctor before adding this product to your pack if you are pregnant, trying for a baby, or breastfeeding.</p>');
        }

        var promise = new Promise(function (resolve) {
          var overlay = document.createElement('div');
          overlay.className = 'sp-result__modal';
          overlay.innerHTML =
            '<div class="sp-result__modal-overlay"></div>' +
            '<div class="sp-result__modal-content sp-result__disclaimer-content">' +
              '<div class="sp-result__disclaimer-body">' + messages.join('') + '</div>' +
              '<div class="sp-result__modal-actions">' +
                '<button class="sp-result__cta-btn" id="sp-addon-disclaimer-confirm" type="button">Add to Pack</button>' +
                '<button class="sp-result__skip-btn" id="sp-addon-disclaimer-cancel" type="button">Cancel</button>' +
              '</div>' +
            '</div>';

          document.getElementById('sp-result').appendChild(overlay);

          var cleanup = function () {
            overlay.parentNode.removeChild(overlay);
          };

          document.getElementById('sp-addon-disclaimer-confirm').addEventListener('click', function () {
            cleanup();
            resolve(true);
          });
          document.getElementById('sp-addon-disclaimer-cancel').addEventListener('click', function () {
            cleanup();
            resolve(false);
          });
          overlay.querySelector('.sp-result__modal-overlay').addEventListener('click', function () {
            cleanup();
            resolve(false);
          });
        });

        return { needed: true, promise: promise };
      }
    },

    // ─── INIT ───────────────────────────────────────────────────────────────
    init: function () {
      // Read token from URL query string
      try {
        var params = new URLSearchParams(window.location.search);
        StarterPackResult.State.token = params.get('token');
      } catch (e) {
        StarterPackResult.State.token = null;
      }

      // Override COLLECTION_ID from personalised config if available
      var collectionId = window.personalised &&
        window.personalised.config &&
        window.personalised.config.bundle &&
        window.personalised.config.bundle.RECHARGE &&
        window.personalised.config.bundle.RECHARGE.COLLECTION_ID;

      if (collectionId) {
        StarterPackResult.Config.COLLECTION_ID = collectionId;
      }

      // Fetch pack data — transitions to reveal on success
      StarterPackResult.Fetcher.fetchPack();
    }
  };

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { StarterPackResult.init(); });
  } else {
    StarterPackResult.init();
  }

})();
